import { SiGmail, SiLinkedin } from "react-icons/si";
import React, { useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { FiArrowRight } from "react-icons/fi";

export const Nav = () => {
  const [active, setActive] = useState(false);

  return (
    <header className="fixed top-0 left-0 right-0 z-[9999] flex justify-between items-center p-0 h-0">
      <Logo /> {/* Logo on the left */}
      <HamburgerButton active={active} setActive={setActive} /> {/* Hamburger on the right */}
      <AnimatePresence>{active && <LinksOverlay />}</AnimatePresence>
    </header>
  );
};

const LinksOverlay = () => {
  return (
    <nav className="fixed right-4 top-4 z-40 h-[calc(100vh_-_32px)] w-[calc(100%_-_32px)] overflow-hidden">
      {/* Animated gradient orbs */}
      <motion.div
        initial={{ opacity: 0, scale: 0 }}
        animate={{ opacity: 1, scale: 1 }}
        exit={{ opacity: 0, scale: 0 }}
        transition={{ duration: 0.5 }}
        className="absolute top-1/4 right-1/4 w-96 h-96 bg-gradient-to-br from-pink-500/30 to-rose-500/30 rounded-full blur-3xl"
      />
      <motion.div
        initial={{ opacity: 0, scale: 0 }}
        animate={{ opacity: 1, scale: 1 }}
        exit={{ opacity: 0, scale: 0 }}
        transition={{ duration: 0.5, delay: 0.1 }}
        className="absolute bottom-1/4 left-1/4 w-96 h-96 bg-gradient-to-br from-indigo-500/30 to-blue-500/30 rounded-full blur-3xl"
      />

      <LinksContainer />
      <FooterCTAs />
    </nav>
  );
};

const LinksContainer = () => {
  return (
    <motion.div className="space-y-4 p-12 pl-4 md:pl-20">
      {LINKS.map((l, idx) => (
        <NavLink key={l.title} href={l.href} idx={idx}>
          {l.title}
        </NavLink>
      ))}
    </motion.div>
  );
};

const NavLink = ({ children, href, idx }) => {
  return (
    <motion.a
      initial={{ opacity: 0, y: -8 }}
      animate={{
        opacity: 1,
        y: 0,
        transition: {
          delay: 0.75 + idx * 0.125,
          duration: 0.5,
          ease: "easeInOut",
        },
      }}
      exit={{ opacity: 0, y: -8 }}
      href={href}
      className="group relative block text-5xl font-bold text-white transition-all hover:pl-4 md:text-7xl"
    >
      {/* Gradient text on hover */}
      <span className="relative inline-block">
        <span className="relative z-10 group-hover:text-transparent group-hover:bg-clip-text group-hover:bg-gradient-to-r group-hover:from-violet-300 group-hover:via-pink-300 group-hover:to-indigo-300 transition-all duration-300">
          {children}
        </span>
        {/* Animated underline */}
        <motion.span
          className="absolute -bottom-2 left-0 h-1 bg-gradient-to-r from-violet-400 via-pink-400 to-indigo-400 rounded-full"
          initial={{ width: 0 }}
          whileHover={{ width: "100%" }}
          transition={{ duration: 0.3 }}
        />
      </span>
      <span className="text-violet-300">.</span>
    </motion.a>
  );
};

const Logo = () => {
  // Add your actual logo SVG here
  return (
    <a href="/" className="h-12 w-12 md:h-16 md:w-16 fixed left-4 top-4" aria-label="Home">
      <svg
        width="80"
        height="80"
        viewBox="0 0 236 236"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
        role="img"
        aria-labelledby="logoTitle"
      >
        <title id="logoTitle">Home</title>
        <g filter="url(#filter0_d_1_46)">
          <rect x="15" y="11" width="207" height="207" rx="18" fill="#8B5CF6" />
          <path
            d="M87.4939 113.748L88.3533 113.577L89.1994 113.361L90.7602 112.827L92.2721 112.13L93.7371 111.268L95.1555 110.244L96.1441 109.402L97.0547 108.507L97.8897 107.559L98.6445 106.554L99.5618 105.087L100.33 103.517L100.606 102.836L100.907 101.963L101.161 101.075L101.365 100.169L101.521 99.2423L101.655 97.9999L101.699 96.7686L101.659 95.5529L101.53 94.3484L101.238 92.8321L100.806 91.338L100.232 89.8663L99.5173 88.4123L98.7736 87.1877L97.9097 86.0232L96.9256 84.9211L95.8212 83.8791L94.7413 83.0085L93.6169 82.2381L92.4524 81.5634L91.2456 80.989L89.7961 80.4457L88.291 80.036L86.7302 79.7555L85.1114 79.6085L82.5153 79.5239L79.2667 79.4927L44.3008 79.5016L44.1405 79.4259L44.0982 79.2567L44.1093 79.2189L44.4366 78.4952L44.7661 77.8985L45.6568 76.5336L46.6097 75.2088L47.625 73.9219L48.7027 72.675L49.8449 71.466L51.0495 70.2948L52.3164 69.1615L53.6457 68.0682L54.9527 67.1019L56.253 66.2803L57.3173 65.7348L57.7648 65.5812L58.1945 65.4899L58.5575 65.4565L60.2764 65.4275L78.5119 65.4632L80.2486 65.4498L84.3255 65.4387L87.1688 65.5099L88.0973 65.5856L89.1215 65.7303L90.237 65.9396L91.446 66.2157L93.4477 66.7746L95.3848 67.4493L97.2573 68.2419L99.0698 69.1503L100.737 70.1256L102.347 71.2055L103.926 72.4056L105.418 73.6814L106.825 75.0329L108.143 76.4579L109.381 77.9631L110.532 79.5462L111.594 81.2027L112.572 82.9372L113.429 84.7007L114.157 86.4864L114.754 88.2988L115.223 90.1335L115.477 91.4115L115.68 92.6851L115.831 93.9543L115.929 95.2167L115.976 96.4725L115.974 97.7238L115.916 98.9707L115.809 100.211L115.549 102.075L115.17 103.927L114.671 105.766L114.057 107.592L113.231 109.567L112.182 111.62L110.957 113.664L109.615 115.59L108.152 117.393L106.571 119.077L105.587 120.009L104.56 120.9L103.494 121.751L102.387 122.559L101.238 123.325L100.047 124.051L98.8159 124.734L97.5446 125.376L96.0906 126.032L94.65 126.598L93.2161 127.077L91.7956 127.466L89.638 127.923L87.4427 128.246L85.2116 128.435L82.945 128.493L68.0738 128.475L67.9536 128.426L67.9023 128.306V114.127L67.9335 114.04L68.0404 113.989L84.5147 113.98L86.0243 113.933L87.4939 113.748Z" fill="white" />
          <path
            d="M132.568 79.7621L132.484 80.134L132.486 80.6015L132.6 82.1357L132.626 83.1443L132.631 85.5178L132.7 87.8935L132.693 104.764L132.706 128.867L132.709 129.281L132.682 131.343L132.546 132.83L132.404 133.596L132.21 134.291L131.967 134.907L131.671 135.457L131.014 136.355L130.228 137.15L129.338 137.824L128.358 138.361L127.336 138.733L126.316 138.933L125.274 138.962L124.27 138.817L123.822 138.695L122.923 138.354L122.106 137.933L121.373 137.43L120.723 136.847L120.135 136.15L119.637 135.364L119.227 134.489L118.911 133.527L118.79 132.948L118.706 132.229L118.641 130.365L118.619 121.014L118.646 120.43L118.735 119.86L118.902 119.384L119.211 118.823L120.289 117.062L121.269 115.331L122.108 113.693L122.863 112.027L123.531 110.335L124.114 108.616L124.488 107.331L124.816 106.033L125.094 104.722L125.328 103.395L125.515 102.052L125.655 100.696L125.748 99.3247L125.795 97.9398V96.3723L125.74 94.8337L125.63 93.3264L125.468 91.8479L125.252 90.3984L124.98 88.9801L124.655 87.5885L124.277 86.2281L123.798 84.7274L123.268 83.2512L122.689 81.795L122.063 80.3633L121.347 78.8737L120.576 77.4087L119.75 75.9703L118.871 74.5564L118.777 74.3382L118.708 74.0109L118.648 73.0312L118.646 69.353L118.69 65.6613L118.744 65.5055L118.9 65.4542L145.108 65.4409L146.671 65.4654L148.228 65.5478L149.773 65.688L151.312 65.8884L153.026 66.1846L154.729 66.5519L156.422 66.9928L158.103 67.5094L160.489 68.3488L162.56 69.1815L164.373 70.0142L165.813 70.7379L167.229 71.5105L168.621 72.3321L169.992 73.1982L171.34 74.1133L172.662 75.0752L173.965 76.0861L175.243 77.1437L176.935 78.6533L178.485 80.1785L180.48 82.3628L182.332 84.6316L184.038 86.9896L185.598 89.4321L186.494 90.9885L187.333 92.5782L188.117 94.2014L188.843 95.858L189.515 97.5457L190.134 99.269L190.695 101.026L191.2 102.814L191.735 105.089L192.142 107.414L192.427 109.792L192.585 112.221L192.646 115.158L192.581 117.852L192.394 120.312L192.084 122.519L191.715 124.402L191.278 126.253L190.78 128.07L190.216 129.853L189.586 131.601L188.894 133.315L188.134 134.996L187.311 136.642L186.148 138.722L184.879 140.745L183.935 142.115L182.938 143.46L181.889 144.78L180.785 146.076L179.629 147.345L178.42 148.59L177.16 149.81L175.844 151.005L174.118 152.448L172.35 153.769L169.955 155.356L167.499 156.792L164.98 158.079L162.402 159.215L160.496 159.945L158.557 160.595L156.586 161.165L154.582 161.657L152.855 162.005L151.06 162.292L149.203 162.515L147.279 162.675L145.416 162.748L142.826 162.768L118.659 162.773L118.532 162.755L118.45 162.699L118.407 162.61L118.41 162.483L118.554 162.145L119.628 160.321L120.801 158.571L121.565 157.54L122.366 156.536L123.206 155.561L124.083 154.61L124.998 153.686L125.951 152.789L126.942 151.921L127.968 151.077L129.467 149.97L130.829 149.093L131.255 148.863L131.676 148.697L132.179 148.574L132.749 148.514L132.833 148.509L133.887 148.465H134.316L139.716 148.514L144.612 148.472L146.896 148.371L149.047 148.155L150.535 147.933L151.993 147.652L153.425 147.316L154.827 146.926L156.203 146.479L157.55 145.978L158.869 145.419L160.16 144.804L161.349 144.177L162.513 143.5L163.653 142.772L164.769 141.995L165.858 141.168L166.924 140.293L167.964 139.369L168.979 138.396L170.444 136.813L171.867 135.021L172.716 133.828L173.506 132.616L174.241 131.389L174.918 130.14L175.539 128.876L176.104 127.593L176.612 126.293L177.062 124.972L177.552 123.278L177.95 121.555L178.257 119.802L178.476 118.021L178.6 116.211L178.636 114.374L178.58 112.506L178.436 110.609L178.231 108.986L177.939 107.372L177.563 105.764L177.102 104.165L176.554 102.575L175.92 100.992L175.2 99.4182L174.397 97.8507L173.163 95.7399L171.809 93.7405L170.342 91.8546L168.754 90.08L167.485 88.822L166.152 87.6263L164.749 86.493L163.282 85.4243L161.756 84.4468L160.078 83.5117L158.238 82.6233L156.246 81.7794L154.409 81.1159L152.572 80.5837L150.739 80.1852L148.907 79.9202L145.705 79.6619L142.049 79.5328L137.701 79.5016L132.9 79.535L132.695 79.5929L132.568 79.7621Z"
            fill="white"
          />
          <path
            d="M44.0893 89.9197L44.1338 89.7839L44.2162 89.7149L44.3231 89.6904L84.3767 89.7594L85.0647 89.795L85.746 89.8952L86.414 90.06L87.0619 90.2916L87.6831 90.5832L88.2732 90.9328L88.8254 91.338L89.333 91.7945L89.7939 92.2977L90.2036 92.8432L90.5576 93.4266L90.8493 94.0411L91.0809 94.6801L91.2479 95.3392L91.3458 96.0116L91.3792 96.6907V96.8955L91.3436 97.5724L91.2412 98.2448L91.0742 98.9017L90.8404 99.5407L90.5443 100.155L90.1902 100.736L89.7783 101.28L89.3152 101.783L88.8053 102.237L88.2509 102.64L87.6609 102.99L87.0374 103.279L86.3895 103.506L85.7215 103.669L85.0402 103.769L84.3522 103.8L44.2986 103.731L44.1583 103.684L44.0915 103.602L44.067 103.497L44.0893 89.9197Z"
            fill="white"
          />
          <path
            d="M46.042 124.037L47.4135 122.41L48.8096 120.891L50.2078 119.502L51.6262 118.228L53.0467 117.084L54.4828 116.055L55.919 115.158L57.364 114.383L57.8583 114.185L58.0209 114.218L58.1032 114.361L58.1055 114.383V162.321L58.0721 162.49L57.9763 162.635L57.8316 162.731L57.6624 162.764H44.3943L44.254 162.744L44.1516 162.684L44.0915 162.581L44.0714 162.441L44.0803 142.155L44.047 127.936L44.116 127.146L44.2073 126.787L44.3921 126.418L45.2849 125.059L46.042 124.037Z"
            fill="white"
          />
        </g>
        <defs>
          <filter
            id="filter0_d_1_46"
            x="0"
            y="0"
            width="236"
            height="236"
            filterUnits="userSpaceOnUse"
            colorInterpolationFilters="sRGB"
          >
            <feFlood floodOpacity="0" result="BackgroundImageFix" />
            <feColorMatrix
              in="SourceAlpha"
              type="matrix"
              values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 127 0"
              result="hardAlpha"
            />
            <feOffset dy="4" />
            <feGaussianBlur stdDeviation="2" />
            <feComposite in2="hardAlpha" operator="out" />
            <feColorMatrix
              type="matrix"
              values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0.25 0"
            />
            <feBlend mode="normal" in2="BackgroundImageFix" result="effect1_dropShadow_1_46" />
            <feBlend mode="normal" in="SourceGraphic" in2="effect1_dropShadow_1_46" result="shape" />
          </filter>
        </defs>
      </svg>
    </a>
  );
};

const HamburgerButton = ({ active, setActive }) => {
  return (
    <>
      <motion.div
        initial={false}
        animate={active ? "open" : "closed"}
        variants={UNDERLAY_VARIANTS}
        style={{ top: 16, right: 16 }}
        className="fixed z-10 rounded-xl bg-gradient-to-br from-violet-600 via-purple-600 to-indigo-600 shadow-2xl shadow-violet-500/30"
      />

      <motion.button
        aria-label="Toggle navigation"
        initial={false}
        animate={active ? "open" : "closed"}
        onClick={() => setActive((pv) => !pv)}
        whileTap={{ scale: 0.95 }}
        className={`group fixed right-4 top-4 z-50 h-20 w-20 transition-all backdrop-blur-sm ${
          active
            ? "rounded-bl-xl rounded-tr-xl bg-white/10"
            : "rounded-xl bg-gradient-to-br from-white/10 to-white/5 hover:from-white/20 hover:to-white/10 border border-white/20"
        }`}
      >
        {/* Animated gradient background on hover */}
        <motion.div
          className="absolute inset-0 rounded-xl bg-gradient-to-br from-violet-500/20 to-indigo-500/20 opacity-0 group-hover:opacity-100 transition-opacity duration-300"
          style={{ zIndex: -1 }}
        />

        <motion.span
          variants={HAMBURGER_VARIANTS.top}
          className="absolute block h-1 w-10 bg-white rounded-full shadow-lg"
          style={{ y: "-50%", left: "50%", x: "-50%" }}
          animate={active ? "open" : "closed"}
        />
        <motion.span
          variants={HAMBURGER_VARIANTS.middle}
          className="absolute block h-1 w-10 bg-white rounded-full shadow-lg"
          style={{ left: "50%", x: "-50%", top: "50%", y: "-50%" }}
          animate={active ? "open" : "closed"}
        />
        <motion.span
          variants={HAMBURGER_VARIANTS.bottom}
          className="absolute block h-1 w-5 bg-white rounded-full shadow-lg"
          style={{ x: "-50%", y: "50%" }}
          animate={active ? "open" : "closed"}
        />
      </motion.button>
    </>
  );
};

const FooterCTAs = () => {
  return (
    <>
      <div className="absolute bottom-6 left-6 flex gap-4 md:flex-col">
        {SOCIAL_CTAS.map((l, idx) => (
          <motion.a
            key={idx}
            href={l.href}
            aria-label={l.Component === SiGmail ? "Gmail" : "LinkedIn"}
            initial={{ opacity: 0, y: -8 }}
            animate={{
              opacity: 1,
              y: 0,
              transition: {
                delay: 1 + idx * 0.125,
                duration: 0.5,
                ease: "easeInOut",
              },
            }}
            exit={{ opacity: 0, y: -8 }}
            className="text-xl text-white transition-colors hover:text-violet-300"
          >
            <l.Component />
          </motion.a>
        ))}
      </div>

      <motion.a
        href="/contact"
        initial={{ opacity: 0, y: 8 }}
        animate={{
          opacity: 1,
          y: 0,
          transition: {
            delay: 1.125,
            duration: 0.5,
            ease: "easeInOut",
          },
        }}
        exit={{ opacity: 0, y: 8 }}
        className="absolute bottom-2 right-2 flex items-center gap-2 rounded-full bg-violet-700 px-3 py-3 text-4xl uppercase text-violet-200 transition-colors hover:bg-white hover:text-violet-600 md:bottom-4 md:right-4 md:px-6 md:text-2xl"
      >
        <span className="hidden md:block">Contact me!</span> <FiArrowRight />
      </motion.a>
    </>
  );
};

const LINKS = [
  { title: "home", href: "/" },
  { title: "about", href: "/about" },
  { title: "portfolio", href: "/portfolio" },
  { title: "services", href: "/services" },
];

const SOCIAL_CTAS = [
  { Component: SiGmail, href: "mailto:info@rplambech.dk" },
  { Component: SiLinkedin, href: "https://www.linkedin.com/in/rasmusplambech/" },
];

const UNDERLAY_VARIANTS = {
  open: {
    width: "calc(100% - 32px)",
    height: "calc(100vh - 32px)",
    transition: { type: "spring", mass: 3, stiffness: 400, damping: 50 },
  },
  closed: {
    width: "80px",
    height: "80px",
    transition: {
      delay: 0.75,
      type: "spring",
      mass: 3,
      stiffness: 400,
      damping: 50,
    },
  },
};

const HAMBURGER_VARIANTS = {
  top: {
    open: { rotate: ["0deg", "0deg", "45deg"], top: ["35%", "50%", "50%"] },
    closed: { rotate: ["45deg", "0deg", "0deg"], top: ["50%", "50%", "35%"] },
  },
  middle: {
    open: { rotate: ["0deg", "0deg", "-45deg"] },
    closed: { rotate: ["-45deg", "0deg", "0deg"] },
  },
  bottom: {
    open: { rotate: ["0deg", "0deg", "45deg"], bottom: ["35%", "50%", "50%"], left: "50%" },
    closed: { rotate: ["45deg", "0deg", "0deg"], bottom: ["50%", "50%", "35%"], left: "calc(50% + 10px)" },
  },
};